import { HelpersMapping } from 'api/client/mapping/helpers-mapping';
import { AirlinesQueryFilter } from 'api/airlines-list';
import { AirlineListQueryDTO, AirlinesClient } from 'api/swagger/ApiClient';
import { autoinject } from 'aurelia-framework';
import { Router } from 'aurelia-router';
import { watch } from 'aurelia-watch-decorator';
import { SortInstruction } from 'framework/aurelia_tools/sort-controller';
import { tryExecute } from 'framework/error_handling/error-utils';
import { TaskCounter } from 'framework/script_utilities/task-executor';
import { TextHighlightValueConverter } from 'framework/value_converters/text-highlight';
import * as E from 'support/errors';
import { BATCH_SIZE, HOME_ROUTE } from 'support/settings';

@autoinject
export class AdminAirlinesListScreen {
  airlines: AirlineListQueryDTO[] = null;
  lastQueryId = 0;
  loaded = false;
  sortInstruction: SortInstruction = null;
  filter: AirlinesQueryFilter = { text: null, includeArchived: false, airlineRating: null, airlineStatus: null };

  constructor(private api: AirlinesClient, private router: Router, private taskCounter: TaskCounter, private helpersMapping: HelpersMapping) { }

  bind() {
    this.queryAirlines();
  }

  filterMutated() {
    this.taskCounter.execute(() => tryExecute(() => this.queryAirlines(), E.FILTER_ERROR_MESSAGE, E.FILTER_ERROR_TITLE, true));
  }

  @watch((x: AdminAirlinesListScreen) => x.sortInstruction.expression)
  sortExpressionChanged() {
    this.taskCounter.execute(() => tryExecute(() => this.queryAirlines(), E.SORT_ERROR_MESSAGE, E.SORT_ERROR_TITLE, true));
  }

  async queryAirlines() {
    const queryId = ++this.lastQueryId;
    const airlines = await this.fetchAirlines(0, this.batchSize);
    if (queryId === this.lastQueryId) {
      this.airlines = airlines;
      this.loaded = true;
    }
  }

  async fetchAirlines(skip: number, take: number) {
    const highlight = this.filter.text;
    const queryResult = await this.api.getAllByFilter(skip, take, this.sortInstruction?.expression ?? null, this.filter.airlineRating != null ? [this.filter.airlineRating] : [], this.filter.airlineStatus != null ? [this.filter.airlineStatus] : [], this.filter.text, this.helpersMapping.mapToApiIsActiveFilter(this.filter.includeArchived));
    return queryResult.data.map(t => this.extendAirline(t, highlight));
  }

  private extendAirline(airline: AirlineListQueryDTO, expression: string) {
    const converter = new TextHighlightValueConverter();
    const highlight = (line: string) => converter.toView(line, expression)?.map(s => s.highlight ? `<span class="--highlight">${s.substring}</span>` : s.substring).join('');
    airline['highlightedName'] = highlight(airline.name);
    airline['highlightedCode'] = highlight(airline.code);
    return airline;
  }

  get homeRoute() {
    return HOME_ROUTE;
  }

  get batchSize() {
    return BATCH_SIZE;
  }

  showAirline(airline: AirlineListQueryDTO) {
    this.router.navigate(`#/${HOME_ROUTE}/admin/airlines/${airline.id}`);
  }

  createNewAirline() {
    this.router.navigate(`#/${HOME_ROUTE}/admin/airlines/new`);
  }
}
